/**
 * Duração legível.
 *
 * O banco guarda tempo como número de minutos ("325") ou como texto
 * ("25 minutos", "1 hora"). Até aqui cada tela tinha a sua própria cópia de
 * `formatTime` — oito ao todo — e todas faziam a mesma coisa: colar " min"
 * no número. O resultado é que a lista de Panadería Cero mostra "502 min"
 * para um pudim que descansa a noite inteira, e "325 min" para um cheesecake.
 *
 * Quem lê "502 min" precisa dividir por 60 de cabeça. O público deste app são
 * pessoas idosas; é uma conta que ninguém deveria ter que fazer para saber se
 * dá tempo de fazer o bolo hoje.
 *
 * A partir de uma hora a duração vira "8 h 20" / "5 h". Abaixo disso continua
 * em minutos, que é como se fala de receita curta.
 */

/** Minutos de um valor cru do banco, ou `null` quando não dá para saber. */
export function minutosDe(valor?: string | number | null): number | null {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) && valor > 0 ? Math.round(valor) : null;

  const s = String(valor).trim();
  if (!s) return null;

  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return n > 0 ? n : null;
  }

  // "1 h 30", "1 hora 30 minutos", "2 std 15 min", "1 hr"
  const horas = s.match(/(\d+(?:[.,]\d+)?)\s*(?:h\b|hr?s?\b|hora|horas|stunde|stunden|std)/i);
  const mins = s.match(/(\d+)\s*(?:min\b|mins?\b|minuto|minutos|minute|minuten)/i);
  if (horas || mins) {
    const h = horas ? parseFloat(horas[1].replace(",", ".")) : 0;
    const m = mins ? Number(mins[1]) : 0;
    const total = Math.round(h * 60 + m);
    return total > 0 ? total : null;
  }

  const solto = s.match(/(\d+)/);
  if (solto) {
    const n = Number(solto[1]);
    return n > 0 ? n : null;
  }
  return null;
}

const ABREV_HORA: Record<string, string> = { es: "h", en: "h", de: "Std" };
const ABREV_MIN: Record<string, string> = { es: "min", en: "min", de: "Min" };

/**
 * "25 min", "1 h", "8 h 20". Devolve `null` quando o valor não diz nada —
 * a tela então simplesmente não mostra tempo, em vez de mostrar "0 min".
 */
export function formatarTempo(valor?: string | number | null, idioma = "es"): string | null {
  const total = minutosDe(valor);
  if (total == null) return null;

  const lang = idioma === "de" ? "de" : idioma === "en" ? "en" : "es";
  const min = ABREV_MIN[lang];
  const hora = ABREV_HORA[lang];

  if (total < 60) return `${total} ${min}`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} ${hora}` : `${h} ${hora} ${m}`;
}
